import type { AdminConnectionView, Channel, DataDeletionRequestView, DataDeletionStatus, UserView } from '@hilo/contracts';
import { DATA_DELETION_NOTE_MAX } from '@hilo/contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type CSSProperties, useId, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import {
  adminActionError, adminKeys, DELETION_STATUS_LABEL, fetchAdminConnections, fetchDeletionRequests, onboardingFailureText,
  revokeCredential, setConnectionAccess, setConnectionMode, startOnboarding, startInstagramOnboarding, updateDeletionRequest,
} from '../../lib/admin';
import { ApiError } from '../../lib/api';
import { CHANNEL_LABEL, errorText } from '../../lib/copy';
import { fetchUsers, keys } from '../../lib/queries';
import { useCurrentUser } from '../../lib/session';
import { formatAgo } from '../../lib/time';
import { hueOf, initialsOf } from '../../ui/Avatar';
import { ChannelMark } from '../../ui/ChannelMark';
import { ConfirmDialog } from '../../ui/ConfirmDialog';
import { IconAlert, IconCheck, IconFlag, IconPause, IconPlug, IconShield, IconUnlink, IconUsers } from '../../ui/icons';
import { useNow } from '../../ui/useNow';
import { useSensitiveAction } from '../auth/ReauthDialog';
import { AdminShell } from './AdminShell';
import styles from './Connections.module.css';

export function actionErrorText(error: unknown): string {
  return (error instanceof ApiError && adminActionError(error.code)) || errorText(error);
}

export function formatDateTime(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat('es-SV', {
    timeZone, day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).format(new Date(iso)).replace('.', '');
}

/** Aviso tras volver del alta (`?alta=error&codigo=…` o `?alta=hecha`). */
function OnboardingNotice() {
  const [params, setParams] = useSearchParams();
  const result = params.get('alta');
  if (result !== 'error' && result !== 'hecha') return null;
  const dismiss = () => setParams({}, { replace: true });
  const ok = result === 'hecha';
  return (
    <div className={styles.notice} data-kind={ok ? 'ok' : 'error'} role={ok ? 'status' : 'alert'}>
      <span className={styles.noticeIcon} aria-hidden="true">{ok ? <IconCheck /> : <IconAlert />}</span>
      <p>{ok ? 'Cuenta conectada. Las conexiones nuevas están en pruebas y sin acceso: concédelo a quien vaya a atenderlas.' : onboardingFailureText(params.get('codigo'))}</p>
      <button type="button" className="btn btn-quiet" onClick={dismiss}>Entendido</button>
    </div>
  );
}

const listFormat = new Intl.ListFormat('es', { style: 'long', type: 'conjunction' });

function AccessDialog({ connection, users, onClose }: { connection: AdminConnectionView; users: UserView[]; onClose: () => void }) {
  const client = useQueryClient();
  const { perform, dialog } = useSensitiveAction();
  const [selected, setSelected] = useState(() => new Set(connection.accessUserIds));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const candidates = users.filter((user) => user.role !== 'ADMIN' && (user.active || selected.has(user.id)))
    .sort((a, b) => a.displayName.localeCompare(b.displayName, 'es'));
  const before = new Set(connection.accessUserIds);
  // A quien gane o pierda el acceso se le cierra la sesión: se nombra antes de guardar.
  const changed = candidates.filter((user) => before.has(user.id) !== selected.has(user.id));
  const granted = candidates.filter((user) => selected.has(user.id)).length;
  const toggle = (id: string) => setSelected((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const done = await perform(() => setConnectionAccess(connection.id, [...selected].filter((id) => candidates.some((user) => user.id === id))));
      if (done === undefined) return;
      await client.invalidateQueries({ queryKey: adminKeys.connections });
      onClose();
    } catch (failure) {
      setError(actionErrorText(failure));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <ConfirmDialog open variant="form" icon={<ChannelMark channel={connection.channel} />} title="Quién atiende esta cuenta"
        subtitle={`${CHANNEL_LABEL[connection.channel]}: ${connection.displayName}`}
        confirmLabel="Guardar cambios" busyLabel="Guardando…" confirmDisabled={changed.length === 0}
        busy={busy} onConfirm={() => void save()} onCancel={onClose}>
        <p>Quien tenga acceso verá y atenderá sus conversaciones. Administración las ve todas.</p>
        <fieldset className={styles.people}>
          <legend className={styles.peopleCount} aria-live="polite">
            {candidates.length === 0 ? 'Personas con acceso' : `${granted} de ${candidates.length} ${candidates.length === 1 ? 'persona' : 'personas'} con acceso`}
          </legend>
          {candidates.length === 0 && <p>No hay personas de atención o supervisión activas.</p>}
          {candidates.map((user) => (
            <label key={user.id} className={styles.person}>
              <input type="checkbox" checked={selected.has(user.id)} onChange={() => toggle(user.id)} disabled={busy} />
              <span className={styles.face} style={{ '--hue': hueOf(user.id) } as CSSProperties} aria-hidden="true">{initialsOf(user.displayName)}</span>
              <span className={styles.personName}>{user.displayName}</span>
              <span className={styles.personRole}>{user.role === 'SUPERVISOR' ? 'Supervisión' : 'Atención'}{user.active ? '' : ' · desactivada'}</span>
            </label>
          ))}
        </fieldset>
        {changed.length > 0
          ? (
            <p className={styles.logoutNote}>
              <IconAlert />
              <span>Al guardar se cerrará la sesión de {listFormat.format(changed.map((user) => user.displayName))} para aplicar el cambio.</span>
            </p>
          )
          : <p className={styles.small}>A quien gane o pierda el acceso se le cierra la sesión para aplicar el cambio.</p>}
        {error && <p className={styles.dialogError} role="alert">{error}</p>}
      </ConfirmDialog>
      {dialog}
    </>
  );
}

function RevokeDialog({ connection, onClose }: { connection: AdminConnectionView; onClose: () => void }) {
  const client = useQueryClient();
  const { perform, dialog } = useSensitiveAction();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const revoke = async () => {
    setBusy(true);
    setError(null);
    try {
      const done = await perform(() => revokeCredential(connection.id));
      if (done === undefined) return;
      await client.invalidateQueries({ queryKey: adminKeys.connections });
      onClose();
    } catch (failure) {
      setError(actionErrorText(failure));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <ConfirmDialog open icon={<IconUnlink />} title={`¿Revocar ${connection.displayName}?`} confirmLabel="Revocar" busyLabel="Revocando…"
        busy={busy} onConfirm={() => void revoke()} onCancel={onClose}>
        <p>{connection.instagramLogin === 'INSTAGRAM_LOGIN' ? 'Hilo desconecta esta cuenta de Instagram y borra su autorización guardada.' : 'Hilo borra el token de la página y desactiva su Messenger y su Instagram.'} Las conversaciones se conservan.</p>
        <p>Para volver a usarla, conéctala otra vez. El permiso en Meta se retira desde la configuración de apps de Facebook o Instagram.</p>
        {error && <p className={styles.dialogError} role="alert">{error}</p>}
      </ConfirmDialog>
      {dialog}
    </>
  );
}

/** Pruebas ↔ producción. En producción Hilo contesta a cualquier cliente, no solo a las cuentas de prueba. */
function ModeDialog({ connection, onClose }: { connection: AdminConnectionView; onClose: () => void }) {
  const client = useQueryClient();
  const { perform, dialog } = useSensitiveAction();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toProduction = connection.mode === 'TEST';
  const change = async () => {
    setBusy(true);
    setError(null);
    try {
      const done = await perform(() => setConnectionMode(connection.id, toProduction ? 'PRODUCTION' : 'TEST'));
      if (done === undefined) return;
      await Promise.all([client.invalidateQueries({ queryKey: adminKeys.connections }), client.invalidateQueries({ queryKey: keys.connections })]);
      onClose();
    } catch (failure) {
      setError(actionErrorText(failure));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <ConfirmDialog open icon={toProduction ? <IconFlag /> : <IconPause />}
        title={toProduction ? `¿Pasar ${connection.displayName} a producción?` : `¿Volver ${connection.displayName} a pruebas?`}
        confirmLabel={toProduction ? 'Pasar a producción' : 'Volver a pruebas'} busyLabel="Cambiando…"
        busy={busy} onConfirm={() => void change()} onCancel={onClose}>
        {toProduction
          ? (
            <>
              <p>Hilo podrá responder a cualquier persona que escriba a esta cuenta, no solo a las cuentas de prueba. Vale desde la siguiente respuesta.</p>
              <p>Hazlo solo cuando Meta haya aprobado la revisión de la app Hilo.</p>
            </>
          )
          : <p>Hilo seguirá recibiendo sus mensajes, pero solo podrá responder a las cuentas de prueba. Las respuestas a clientes que estén en cola no saldrán.</p>}
        {error && <p className={styles.dialogError} role="alert">{error}</p>}
      </ConfirmDialog>
      {dialog}
    </>
  );
}

type Health = { tone: 'ok' | 'idle' | 'warning' | 'off'; text: string };

/** Una sola respuesta a «¿funciona esta cuenta?», de lo más grave a lo normal. */
function healthOf(connection: AdminConnectionView): Health {
  if (!connection.enabled) return { tone: 'off', text: 'Desactivada' };
  if (connection.origin === 'CONSENT' && !connection.credentialsPresent) return { tone: 'off', text: 'Revocada: conéctala otra vez para usarla' };
  if (!connection.credentialsPresent) return { tone: 'warning', text: 'Falta la credencial en el servidor: no puede responder' };
  // Instagram Login: el token dura 60 días y lo renueva el trabajador.
  if (connection.tokenRefreshFailure === 'TOKEN_EXPIRED') return { tone: 'off', text: 'La autorización caducó: conéctala otra vez' };
  if (connection.tokenRefreshFailure) return { tone: 'warning', text: 'Falló la renovación de la autorización; se reintentará sola' };
  if (!connection.lastWebhookAt) return { tone: 'idle', text: 'Esperando el primer mensaje' };
  return { tone: 'ok', text: 'Recibe mensajes' };
}

const firstName = (name: string) => name.trim().split(/\s+/)[0] ?? name;

/** Quién atiende la cuenta: iniciales apiladas y los nombres; si nadie, se dice. */
function AccessPeople({ people }: { people: Array<{ id: string; name: string }> }) {
  if (people.length === 0) return <span className={styles.nobody}>Nadie la atiende todavía</span>;
  const shown = people.slice(0, 3);
  const full = listFormat.format(people.map((person) => person.name));
  return (
    <span className={styles.who} title={full}>
      <span className={styles.stack} aria-hidden="true">
        {shown.map((person) => (
          <span key={person.id} className={styles.face} style={{ '--hue': hueOf(person.id) } as CSSProperties}>{initialsOf(person.name)}</span>
        ))}
        {people.length > shown.length && <span className={styles.face} data-more>+{people.length - shown.length}</span>}
      </span>
      <span className={styles.peopleNames} aria-hidden="true">{listFormat.format(people.map((person) => firstName(person.name)))}</span>
      <span className="visually-hidden">La atienden {full}</span>
    </span>
  );
}

function ConnectionRow({ connection, users, timeZone, now, onAccess, onRevoke, onMode }: {
  connection: AdminConnectionView; users: UserView[]; timeZone: string; now: number; onAccess: () => void; onRevoke: () => void; onMode: () => void;
}) {
  const people = connection.accessUserIds.map((id) => ({ id, name: users.find((user) => user.id === id)?.displayName ?? 'Persona sin nombre' }));
  const consent = connection.origin === 'CONSENT';
  const health = healthOf(connection);
  // WhatsApp sigue en pruebas hasta tener el número de la empresa y el envío por BSUID (el servidor también lo impide).
  const canPromote = connection.mode === 'TEST' && connection.enabled && connection.channel !== 'WHATSAPP';
  return (
    <li className={styles.row}>
      <ChannelMark channel={connection.channel} />
      <div className={styles.rowMain}>
        <div className={styles.rowTitle}>
          <h3 className={styles.rowName}>{connection.displayName}</h3>
          <span className={styles.mode} data-mode={connection.mode}>{connection.mode === 'TEST' ? 'Pruebas' : 'Producción'}</span>
        </div>
        <p className={styles.health} data-tone={health.tone}>
          <span className={styles.healthDot} aria-hidden="true" />
          <span className={styles.healthText}>{health.text}</span>
        </p>
        <p className={styles.origin}>
          <span>
            {!consent
              ? 'Configurada en el servidor'
              : connection.grantedAt && connection.grantedBy
                ? `Conectada con Meta por ${connection.grantedBy.displayName} el ${formatDateTime(connection.grantedAt, timeZone)}`
                : 'Conectada con Meta y revocada después'}
          </span>
          {connection.instagramLogin === 'INSTAGRAM_LOGIN' && <span>Instagram Login: solo texto</span>}
          {connection.tokenExpiresAt && <span>Autorización vigente hasta el {formatDateTime(connection.tokenExpiresAt, timeZone)}</span>}
          {connection.lastWebhookAt && (
            <span>
              Último aviso de Meta{' '}
              <time dateTime={connection.lastWebhookAt} title={formatDateTime(connection.lastWebhookAt, timeZone)}>
                {formatAgo(connection.lastWebhookAt, timeZone, new Date(now))}
              </time>
            </span>
          )}
        </p>
      </div>
      <div className={styles.rowSide}>
        <AccessPeople people={people} />
        <div className={styles.rowActions}>
          <button type="button" className={`btn btn-secondary ${styles.compact}`} onClick={onAccess}>
            <IconUsers />Accesos<span className="visually-hidden"> de {connection.displayName}</span>
          </button>
          {canPromote && (
            <button type="button" className={`btn btn-secondary ${styles.compact}`} onClick={onMode}>
              <IconFlag />Pasar a producción<span className="visually-hidden"> {connection.displayName}</span>
            </button>
          )}
          {connection.mode === 'PRODUCTION' && (
            <button type="button" className={`btn btn-quiet ${styles.compact}`} onClick={onMode}>
              <IconPause />Volver a pruebas<span className="visually-hidden"> {connection.displayName}</span>
            </button>
          )}
          {consent && connection.credentialsPresent && (
            <button type="button" className={`btn btn-quiet ${styles.danger}`} onClick={onRevoke}>
              <IconUnlink />Revocar<span className="visually-hidden"> {connection.displayName}</span>
            </button>
          )}
        </div>
      </div>
    </li>
  );
}

const CHANNEL_ORDER: Channel[] = ['WHATSAPP', 'INSTAGRAM', 'MESSENGER'];
const accounts = (count: number) => (count === 1 ? '1 cuenta' : `${count} cuentas`);

function RowsSkeleton() {
  return (
    <ul className={styles.panel} aria-hidden="true">
      {[0, 1, 2].map((index) => (
        <li key={index} className={`${styles.row} ${styles.bone}`}><span /><span><span /><span /></span></li>
      ))}
    </ul>
  );
}

function DeletionDialog({ request, admins, onClose }: { request: DataDeletionRequestView; admins: UserView[]; onClose: () => void }) {
  const client = useQueryClient();
  const ids = { responsible: useId(), status: useId(), note: useId() };
  const [responsibleId, setResponsibleId] = useState(request.responsible?.id ?? '');
  const [status, setStatus] = useState<DataDeletionStatus>(request.status);
  const [note, setNote] = useState(request.publicNote ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await updateDeletionRequest(request.id, { responsibleId: responsibleId || null, status, publicNote: note.trim() || null });
      await client.invalidateQueries({ queryKey: adminKeys.deletionRequests });
      onClose();
    } catch (failure) {
      setError(actionErrorText(failure));
    } finally {
      setBusy(false);
    }
  };
  return (
    <ConfirmDialog open icon={<IconShield />} title={`Solicitud ${request.confirmationCode}`} confirmLabel="Guardar" busyLabel="Guardando…"
      busy={busy} onConfirm={() => void save()} onCancel={onClose}>
      <div className={styles.form}>
        <label className="field" htmlFor={ids.responsible}>
          <span>Responsable</span>
          <select id={ids.responsible} className="input" value={responsibleId} disabled={busy} onChange={(event) => setResponsibleId(event.target.value)}>
            <option value="">Sin asignar</option>
            {admins.map((admin) => <option key={admin.id} value={admin.id}>{admin.displayName}</option>)}
          </select>
        </label>
        <label className="field" htmlFor={ids.status}>
          <span>Estado</span>
          <select id={ids.status} className="input" value={status} disabled={busy} onChange={(event) => setStatus(event.target.value as DataDeletionStatus)}>
            {(Object.keys(DELETION_STATUS_LABEL) as DataDeletionStatus[]).map((value) => <option key={value} value={value}>{DELETION_STATUS_LABEL[value]}</option>)}
          </select>
        </label>
        <label className="field" htmlFor={ids.note}>
          <span>Nota pública</span>
          <textarea id={ids.note} className={`input ${styles.textarea}`} value={note} maxLength={DATA_DELETION_NOTE_MAX} disabled={busy}
            onChange={(event) => setNote(event.target.value)} />
        </label>
        <p className={styles.small}>La ve quien consulte el código. Sin datos personales. Si se conservan datos, explica cuáles y por qué.</p>
      </div>
      {error && <p className={styles.dialogError} role="alert">{error}</p>}
    </ConfirmDialog>
  );
}

function DeletionRequests({ users, timeZone }: { users: UserView[]; timeZone: string }) {
  const requests = useQuery({ queryKey: adminKeys.deletionRequests, queryFn: fetchDeletionRequests });
  const [editing, setEditing] = useState<DataDeletionRequestView | null>(null);
  const admins = users.filter((user) => user.role === 'ADMIN' && user.active);
  return (
    <section className={styles.section} aria-labelledby="solicitudes">
      <div className={styles.sectionHead}>
        <h2 id="solicitudes" className={styles.sectionTitle}>Solicitudes de eliminación de datos</h2>
        <p className={styles.sectionLead}>
          Llegan de Meta cuando alguien que autorizó una página pide borrar sus datos. Hilo borra solo sus tokens y su identificador;
          el resto lo revisa la persona responsable.
        </p>
      </div>
      {requests.isPending && <p className={styles.muted}>Cargando solicitudes…</p>}
      {requests.isError && <p className={styles.muted} role="alert">{errorText(requests.error)}</p>}
      {requests.data?.length === 0 && (
        <div className={styles.emptyPanel}>
          <span className={styles.emptyIcon} aria-hidden="true"><IconShield /></span>
          <p>No hay solicitudes. Cuando llegue una, aparecerá aquí con su código de confirmación.</p>
        </div>
      )}
      {requests.data && requests.data.length > 0 && (
        <ul className={styles.panel}>
          {requests.data.map((request) => (
            <li key={request.id} className={styles.request}>
              <span className={styles.code}>{request.confirmationCode}</span>
              <span className={styles.requestStatus} data-tone={request.status === 'IN_PROGRESS' ? 'warning' : 'ok'}>{DELETION_STATUS_LABEL[request.status]}</span>
              <span className={styles.muted}>Recibida el {formatDateTime(request.receivedAt, timeZone)}</span>
              <span className={styles.muted}>{request.responsible ? `Responsable: ${request.responsible.displayName}` : 'Sin responsable'}</span>
              <button type="button" className={`btn btn-secondary ${styles.compact}`} onClick={() => setEditing(request)}>
                Gestionar<span className="visually-hidden"> la solicitud {request.confirmationCode}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {editing && <DeletionDialog request={editing} admins={admins} onClose={() => setEditing(null)} />}
    </section>
  );
}

/** Conexiones (solo ADMIN): qué cuentas atiende Hilo, de dónde sale su credencial y quién las atiende. */
export function ConnectionsPage() {
  const user = useCurrentUser();
  const connections = useQuery({ queryKey: adminKeys.connections, queryFn: fetchAdminConnections });
  const users = useQuery({ queryKey: keys.users, queryFn: fetchUsers });
  const { perform, dialog } = useSensitiveAction();
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [access, setAccess] = useState<AdminConnectionView | null>(null);
  const [revoking, setRevoking] = useState<AdminConnectionView | null>(null);
  const [switching, setSwitching] = useState<AdminConnectionView | null>(null);
  const people = users.data ?? [];
  const now = useNow(60_000);

  const connect = async (instagram = false) => {
    setStarting(true);
    setStartError(null);
    try {
      const view = await perform(instagram ? startInstagramOnboarding : startOnboarding);
      if (!view) return setStarting(false);
      // El diálogo oficial; el backend valida el retorno y redirige dentro de Conexiones.
      window.location.assign(view.authorizeUrl);
    } catch (error) {
      setStartError(actionErrorText(error));
      setStarting(false);
    }
  };

  const data = connections.data ?? [];
  const attention = data.filter((connection) => healthOf(connection).tone !== 'ok' || connection.accessUserIds.length === 0).length;

  return (
    <AdminShell>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Conexiones</h1>
          <p className={styles.lead}>Las páginas y cuentas de WhatsApp, Instagram y Messenger que atiende Hilo, y quién atiende cada una.</p>
          {data.length > 0 && (
            <p className={styles.summary}>
              <span>{accounts(data.length)}</span>
              {attention > 0
                ? <span className={styles.summaryWarn}>{attention === 1 ? '1 necesita revisión' : `${attention} necesitan revisión`}</span>
                : <span className={styles.summaryOk}>Todas funcionan</span>}
            </p>
          )}
        </div>
        <div className={styles.connectActions}>
          <Link to="/conexiones/whatsapp" className={`btn btn-secondary ${styles.connect}`}><ChannelMark channel="WHATSAPP" />Conectar WhatsApp</Link>
          <button type="button" className={`btn btn-primary ${styles.connect}`} disabled={starting} aria-busy={starting} onClick={() => void connect()}>
            <IconPlug />{starting ? 'Abriendo Meta…' : 'Conectar Facebook e Instagram'}
          </button>
          <button type="button" className={`btn btn-secondary ${styles.connect}`} disabled={starting} onClick={() => void connect(true)}>
            <IconPlug />Conectar Instagram (inicio de sesión de Instagram)
          </button>
        </div>
      </header>
      {startError && <div className={styles.notice} data-kind="error" role="alert"><span className={styles.noticeIcon} aria-hidden="true"><IconAlert /></span><p>{startError}</p></div>}
      <OnboardingNotice />
      {connections.isPending && <><p className="visually-hidden" role="status">Cargando conexiones…</p><RowsSkeleton /></>}
      {connections.isError && <p className={styles.muted} role="alert">{errorText(connections.error)}</p>}
      {connections.isSuccess && data.length === 0 && (
        <div className={styles.emptyPanel}>
          <span className={styles.emptyIcon} aria-hidden="true"><IconPlug /></span>
          <p>Todavía no hay cuentas. Conecta una página de Facebook para empezar a recibir sus mensajes aquí.</p>
        </div>
      )}
      {CHANNEL_ORDER.map((channel) => {
        const items = data.filter((connection) => connection.channel === channel)
          .sort((a, b) => a.displayName.localeCompare(b.displayName, 'es'));
        if (items.length === 0) return null;
        return (
          <section key={channel} className={styles.section} aria-labelledby={`canal-${channel}`}>
            <h2 id={`canal-${channel}`} className={styles.groupTitle}>
              {CHANNEL_LABEL[channel]}
              <span className={styles.count}>{accounts(items.length)}</span>
            </h2>
            <ul className={styles.panel}>
              {items.map((connection) => (
                <ConnectionRow key={connection.id} connection={connection} users={people} timeZone={user.timezone} now={now}
                  onAccess={() => setAccess(connection)} onRevoke={() => setRevoking(connection)} onMode={() => setSwitching(connection)} />
              ))}
            </ul>
          </section>
        );
      })}
      <DeletionRequests users={people} timeZone={user.timezone} />
      {access && <AccessDialog connection={access} users={people} onClose={() => setAccess(null)} />}
      {revoking && <RevokeDialog connection={revoking} onClose={() => setRevoking(null)} />}
      {switching && <ModeDialog connection={switching} onClose={() => setSwitching(null)} />}
      {dialog}
    </AdminShell>
  );
}
